/**
 * Devuelve la fecha local en formato YYYY-MM-DD.
 */
export function fechaLocalYMD(date: string | Date = new Date()): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('sv-SE');
}

/**
 * Formatea una cadena ISO/datetime como hora legible en Costa Rica (HH:MM).
 * Fuerza timeZone 'America/Costa_Rica' para evitar desfases por UTC naive.
 */
export function formatTimeCR(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Costa_Rica',
  });
}

/**
 * Formatea una cadena ISO/datetime como fecha legible en Costa Rica.
 * Fuerza timeZone 'America/Costa_Rica' para evitar desfases por UTC naive.
 */
export function formatDateCR(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-CR', {
    timeZone: 'America/Costa_Rica',
  });
}

/**
 * Devuelve el día operativo en formato YYYY-MM-DD.
 * El día operativo es la fecha calendario Costa Rica (00:00–23:59).
 * Alineado con backend: dia_operativo() = fecha CR pura.
 */
export function diaOperativo(): string {
  return fechaLocalYMD(new Date());
}

/**
 * Parsea un ISO date string del backend. Si viene naive (sin Z ni offset),
 * lo trata como UTC explícito — el backend guarda en UTC con func.now()
 * y Pydantic serializa naive. Sin esto, JS lo interpreta como hora local
 * y produce desfases (ej: +6h en Costa Rica UTC-6).
 *
 * Ejemplos:
 *   "2026-05-11T20:41:19"        → trata como 20:41 UTC → 14:41 CR ✅
 *   "2026-05-11T20:41:19Z"       → ya tiene Z, sin cambio  ✅
 *   "2026-05-11T20:41:19-06:00"  → ya tiene offset, sin cambio ✅
 */
export function parseBackendDate(raw: string): Date {
  if (!raw) return new Date();
  return /Z$|[+-]\d{2}:\d{2}$/.test(raw) ? new Date(raw) : new Date(raw + 'Z');
}

/**
 * Zona horaria oficial del negocio. No depender del dispositivo del usuario.
 * Costa Rica no observa DST, offset fijo UTC-6.
 */
export const CR_TIMEZONE = 'America/Costa_Rica';

/**
 * Fecha de HOY (YYYY-MM-DD) en zona Costa Rica, sin importar la zona del dispositivo.
 *
 * Usar para filtros de fecha de negocio (creditos, movimientos). NO usar
 * `new Date().toISOString().slice(0,10)`: eso da la fecha UTC y despues de las
 * 6:00 p.m. CR ya "rueda" al dia siguiente.
 */
export function fechaCRYMD(date: Date = new Date()): string {
  // 'en-CA' produce formato YYYY-MM-DD; timeZone fuerza Costa Rica.
  return date.toLocaleDateString('en-CA', { timeZone: CR_TIMEZONE });
}

/**
 * Formatea un datetime del backend en hora local de Costa Rica.
 * Si el backend envía un ISO naive (sin offset), se interpreta como UTC
 * antes de convertirlo a la zona oficial del negocio.
 */
export function formatBackendDateTimeCR(
  raw?: string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!raw) return '-';
  const date = parseBackendDate(raw);
  const formatOptions = options ?? {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return date.toLocaleString('es-CR', {
    ...formatOptions,
    timeZone: CR_TIMEZONE,
  });
}

/**
 * Formatea hora HH:MM:SS en 24h forzando zona horaria de Costa Rica,
 * independiente de la configuración del dispositivo.
 */
export function formatHoraCR(d: Date): string {
  return d.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: CR_TIMEZONE,
  });
}

/**
 * Formatea fecha DD/MM/YYYY forzando zona horaria de Costa Rica.
 */
export function formatFechaCR(d: Date): string {
  return d.toLocaleDateString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: CR_TIMEZONE,
  });
}
